// VerifyEmailNeededScreen — Phase 14 §S1 app-shell gate.
//
// When the server reports `me.emailVerified === false`, the SPA bounces
// every authenticated route into this surface. The user can:
//   - resend the verification email (one-tap; POSTs to
//     /api/auth/resend-verification, which is rate-limited server-side)
//   - log out (drops back to the marketing site)
//   - reach the legal pages (privacy, terms, disclaimer) — those routes
//     stay open in the parent routes.tsx wiring even while this gate is up
//
// We deliberately do NOT poll /api/auth/me in the background; the user
// hits "I've verified" after clicking the email link and the SPA does
// a manual refresh(). If the link landed them on /app directly (the
// verify-email handler issues a session cookie on success), the
// AuthProvider's existing mount-time refresh handles the transition.

import { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { Button } from '../../components/ui/Button';
import { Alert } from '../../components/ui/Alert';
import { Wordmark } from '../../components/brand/Wordmark';
import { api, ApiClientError } from '../../lib/api';

type ResendState = 'idle' | 'sending' | 'sent' | 'error' | 'rate_limited';

export function VerifyEmailNeededScreen() {
  const { me, emailVerified, refresh, logout } = useAuth();
  const [resend, setResend] = useState<ResendState>('idle');
  const [refreshing, setRefreshing] = useState(false);

  // Guard: if the gate doesn't apply (or the user has already verified
  // since this screen mounted), drop them back into the app.
  if (emailVerified) {
    return <Navigate to="/app" replace />;
  }

  async function onResend() {
    setResend('sending');
    try {
      await api('/api/auth/resend-verification', { method: 'POST' });
      setResend('sent');
    } catch (err) {
      if (err instanceof ApiClientError && err.status === 429) {
        setResend('rate_limited');
      } else {
        setResend('error');
      }
    }
  }

  async function onCheckAgain() {
    setRefreshing(true);
    try {
      await refresh();
    } finally {
      setRefreshing(false);
    }
  }

  return (
    <main id="main-content" className="container" style={{ paddingBlock: 'var(--space-7)' }}>
      <div
        style={{
          paddingBlockStart: 'var(--space-7)',
          paddingBlockEnd: 'var(--space-6)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Wordmark variant="stacked" size="large" />
        <p className="muted" style={{ marginBlockStart: 'var(--space-2)' }}>
          Verify your email
        </p>
      </div>

      <div
        className="stack"
        style={{ '--stack-gap': 'var(--space-4)' } as React.CSSProperties}
      >
        <Alert variant="info" title="Check your inbox">
          We sent a verification link to{' '}
          <strong>{me?.email ?? 'your email'}</strong>. Click it to finish
          setting up your account. Until then, the app is read-locked.
        </Alert>

        {resend === 'sent' ? (
          <Alert variant="success" title="Email sent">
            We re-sent the verification link. Give it a minute — and check
            your spam folder if it doesn&apos;t arrive.
          </Alert>
        ) : null}

        {resend === 'rate_limited' ? (
          <Alert variant="warning" title="Slow down">
            You&apos;ve requested too many resends. Try again in a few minutes.
          </Alert>
        ) : null}

        {resend === 'error' ? (
          <Alert variant="danger" title="Couldn&apos;t resend">
            Something went wrong. Try again in a moment.
          </Alert>
        ) : null}

        <Button
          type="button"
          size="lg"
          block
          loading={refreshing}
          onClick={onCheckAgain}
        >
          I&apos;ve verified — check again
        </Button>

        <Button
          type="button"
          variant="tertiary"
          block
          loading={resend === 'sending'}
          onClick={onResend}
          disabled={resend === 'sent' || resend === 'rate_limited'}
        >
          Resend verification email
        </Button>

        <Button type="button" variant="tertiary" block onClick={() => void logout()}>
          Sign out
        </Button>

        <p
          className="dim text-caption"
          style={{ marginBlockStart: 'var(--space-6)', textAlign: 'center' }}
        >
          By using this app you agree to the{' '}
          <Link to="/terms" style={{ color: 'var(--color-accent)' }}>
            terms
          </Link>{' '}
          and{' '}
          <Link to="/privacy" style={{ color: 'var(--color-accent)' }}>
            privacy
          </Link>{' '}
          policies.
        </p>
      </div>
    </main>
  );
}

